import React from 'react';
import { Icon, ServerLogo } from '../ui';
import type { AgentMcpAccess } from '../../lib/agentsApi';
import './agentConfigGraph.css';
import { useTranslation } from 'react-i18next';

interface Props {
  agentName: string;
  managed: boolean;
  access: AgentMcpAccess[];
  apiUrl: string;
}

const CENTER_W = 200;
const CENTER_H = 68;
const NODE_W = 210;
const NODE_H = 60;
const GAP_X = 140;
const ROW_H = 88;
const PAD = 24;

/**
 * Hub-and-spoke render of one agent's live MCP access: the agent as a center
 * node, its authorized MCP servers fanning out around it, with tool-access /
 * tool-count shown as a pill on each connector. HTML nodes + pill (not SVG
 * text) so labels never clip against a node edge; an absolutely-positioned
 * SVG layer underneath draws only the connector curves.
 */
const AgentConfigGraph: React.FC<Props> = ({ agentName, managed, access, apiUrl }) => {
  const { t } = useTranslation();
  const n = access.length;

  if (n === 0) {
    return (
      <div className="agent-cfg-empty">
        {t('components.agents.agentConfigGraph.noMCPServersAreCurrentlyAuthorized')}
      </div>
    );
  }

  const height = PAD * 2 + n * ROW_H;
  const width = PAD * 2 + CENTER_W + GAP_X + NODE_W;
  const centerX = PAD;
  const centerY = height / 2 - CENTER_H / 2;
  const spokeX = PAD + CENTER_W + GAP_X;

  const rows = access.map((_, i) => PAD + i * ROW_H + (ROW_H - NODE_H) / 2);

  return (
    <div className="agent-cfg-graph" style={{ width, height }}>
      <svg className="agent-cfg-lines" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <marker id="agent-cfg-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" className="agent-cfg-arrowhead" />
          </marker>
        </defs>
        {access.map((a, i) => {
          const x1 = centerX + CENTER_W;
          const y1 = centerY + CENTER_H / 2;
          const x2 = spokeX;
          const y2 = rows[i] + NODE_H / 2;
          const path = `M${x1},${y1} C${x1 + GAP_X / 2},${y1} ${x2 - GAP_X / 2},${y2} ${x2 - 6},${y2}`;
          return <path key={a.server_id} d={path} className="agent-cfg-edge" markerEnd="url(#agent-cfg-arrow)" />;
        })}
      </svg>

      {/* Center node: the agent */}
      <div
        className={`agent-cfg-node agent-cfg-node--agent ${managed ? 'is-managed' : 'is-staged'}`}
        style={{ left: centerX, top: centerY, width: CENTER_W, height: CENTER_H }}
      >
        <span className="agent-cfg-icon agent-cfg-icon--agent">
          <Icon name="chip" size={18} />
        </span>
        <span className="agent-cfg-node-text">
          <span className="agent-cfg-node-name" title={agentName}>{agentName}</span>
          <span className={`agent-cfg-node-sub ${managed ? 'ok' : ''}`}>{managed ? t('components.agents.agentConfigGraph.managed') : t('components.agents.agentConfigGraph.staged')}</span>
        </span>
      </div>

      {/* Spokes: MCP servers, each with a tool-access/count pill on its edge */}
      {access.map((a, i) => {
        const midX = centerX + CENTER_W + GAP_X / 2;
        const midY = (centerY + CENTER_H / 2 + rows[i] + NODE_H / 2) / 2;
        return (
          <React.Fragment key={a.server_id}>
            <div
              className="agent-cfg-pill"
              style={{ left: midX, top: midY }}
            >
              {a.tool_access === 'read_only' ? t('components.agentConfigGraph.readOnly') : t('components.agentConfigGraph.readWrite')} · {t('components.agentConfigGraph.toolsCount', { count: a.tool_count })}
            </div>
            <div
              className="agent-cfg-node agent-cfg-node--mcp"
              style={{ left: spokeX, top: rows[i], width: NODE_W, height: NODE_H }}
            >
              <span className="agent-cfg-icon agent-cfg-icon--mcp">
                <ServerLogo hostname={a.hostname} apiUrl={apiUrl} size={18} />
              </span>
              <span className="agent-cfg-node-text">
                <span className="agent-cfg-node-name" title={a.server_name}>{a.server_name}</span>
                <span className="agent-cfg-node-sub">{a.hostname}</span>
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default AgentConfigGraph;
